'use strict';

function escapeHtml(text) {
  if (typeof text !== 'string') {
    return text;
  }
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

class AddToCartPanel {
  constructor(productCard) {
    this.productCard = productCard;
    this.panel = productCard.querySelector('.product-card__add-to-cart-panel');

    if (!this.panel) {
      console.warn('AddToCartPanel: Panel element not found');
      return;
    }

    productCard.addToCartPanelInstance = this;

    this.quickAddHeader = this.panel.querySelector('.quick-add-header');
    this.variantContainer = this.panel.querySelector('.variant-selector-container');
    this.addToCartBtn = this.panel.querySelector('.btn-add-to-cart');

    if (!this.quickAddHeader || !this.variantContainer || !this.addToCartBtn) {
      console.warn('AddToCartPanel: Required child elements not found');
      return;
    }

    try {
      this.variants = JSON.parse(this.panel.dataset.productVariants || '[]');
      this.productOptions = JSON.parse(this.panel.dataset.productOptions || '[]');
    } catch (error) {
      console.error('AddToCartPanel: Error parsing product data', error);
      this.variants = [];
      this.productOptions = [];
    }

    this.currentStep = 0;
    this.selectedOptions = {};
    this.selectedVariant = null;

    this.init();
  }

  init() {
    this.cleanup();
    this.setupVariantSelector();
    this.bindEvents();
  }

  cleanup() {
    if (this.variantContainer) {
      const existingListener = this.variantContainer._clickListener;
      if (existingListener) {
        this.variantContainer.removeEventListener('click', existingListener);
      }
    }

    if (this.quickAddHeader) {
      const existingListener = this.quickAddHeader._clickListener;
      if (existingListener) {
        this.quickAddHeader.removeEventListener('click', existingListener);
      }
    }

    if (this.addToCartBtn) {
      const existingListener = this.addToCartBtn._clickListener;
      if (existingListener) {
        this.addToCartBtn.removeEventListener('click', existingListener);
      }
    }
  }

  setupVariantSelector() {
    this.resetUIState();

    if (this.variants.length === 0) {
      return;
    }

    const realVariants = this.variants.filter(
      (variant) => variant.title !== 'Default Title' && variant.options.some((option) => option !== 'Default Title'),
    );

    if (realVariants.length === 0) {
      this.setupDirectAddToCart();
      return;
    }

    const options = this.getProductOptions(realVariants);

    if (options.length === 1) {
      this.setupSingleOptionSelector(options[0], realVariants);
    } else {
      this.setupMultiStepSelector(options, realVariants);
    }
  }

  resetUIState() {
    const quickAddTitle = this.quickAddHeader.querySelector('.quick-add-title');
    const navigation = this.quickAddHeader.querySelector('.variant-navigation');
    const addToCartActions = this.panel.querySelector('.add-to-cart-actions');

    if (this.quickAddHeader) this.quickAddHeader.style.display = 'flex';
    if (quickAddTitle) quickAddTitle.style.display = 'block';
    if (navigation) navigation.style.display = 'none';
    if (addToCartActions) addToCartActions.style.display = 'block';

    this.selectedOptions = {};
    this.selectedVariant = null;
    this.currentStep = 0;
  }

  getProductOptions(variants) {
    const optionsMap = new Map();

    variants.forEach((variant) => {
      variant.options.forEach((value, index) => {
        if (value && value !== null && value !== 'Default Title') {
          const position = index + 1;
          if (!optionsMap.has(position)) {
            optionsMap.set(position, {
              name: this.getOptionName(position),
              values: new Set(),
            });
          }
          optionsMap.get(position).values.add(value);
        }
      });
    });

    return Array.from(optionsMap.entries()).map(([position, data]) => ({
      position,
      name: data.name,
      values: Array.from(data.values),
    }));
  }

  getOptionName(position) {
    if (this.productOptions && this.productOptions.length > 0) {
      const option = this.productOptions.find((opt) => opt.position === position);
      if (option && option.name) {
        return option.name;
      }
    }

    const defaultNames = {
      1: 'Size',
      2: 'Color',
      3: 'Style',
    };
    return defaultNames[position] || `Option ${position}`;
  }

  setupDirectAddToCart() {
    this.selectedVariant = this.variants[0];
    this.addToCartBtn.disabled = !this.selectedVariant.available;
    this.addToCartBtn.style.display = 'block';
    this.variantContainer.innerHTML = '';

    const quickAddTitle = this.quickAddHeader.querySelector('.quick-add-title');
    const navigation = this.quickAddHeader.querySelector('.variant-navigation');
    const addToCartActions = this.panel.querySelector('.add-to-cart-actions');

    if (quickAddTitle) quickAddTitle.style.display = 'block';
    if (navigation) navigation.style.display = 'none';
    if (addToCartActions) addToCartActions.style.display = 'block';

    if (this.quickAddHeader) this.quickAddHeader.style.display = 'flex';

    this.ensureNavigationExists();
  }

  setupSingleOptionSelector(option, variants) {
    this.addToCartBtn.style.display = 'none';

    const html = `
      <div class="variant-step active">
        <div class="variant-options">
          ${option.values
            .map((value) => {
              const variant = this.findVariantByOptions({ [option.position]: value }, variants);
              const isAvailable = variant && variant.available;
              return `
              <button type="button" 
                      class="variant-option ${!isAvailable ? 'unavailable' : ''}" 
                      data-option-position="${option.position}" 
                      data-option-value="${escapeHtml(value)}"
                      data-variant-id="${variant ? variant.id : ''}"
                      ${!isAvailable ? 'disabled' : ''}>
                ${escapeHtml(value)}
              </button>
            `;
            })
            .join('')}
        </div>
      </div>
    `;
    this.variantContainer.innerHTML = html;

    this.ensureNavigationExists();
  }

  setupMultiStepSelector(options, variants) {
    this.addToCartBtn.style.display = 'none';

    const stepsHtml = options
      .map(
        (option, index) => `
      <div class="variant-step ${index === 0 ? 'active' : ''}" data-step="${index}">
        <div class="variant-options">
          ${option.values
            .map(
              (value) => `
            <button type="button" 
                    class="variant-option" 
                    data-option-position="${option.position}" 
                    data-option-value="${escapeHtml(value)}">
              ${escapeHtml(value)}
            </button>
          `,
            )
            .join('')}
        </div>
      </div>
    `,
      )
      .join('');

    this.variantContainer.innerHTML = stepsHtml;

    this.ensureNavigationExists();
    this.variants = variants;
    const firstStep = this.variantContainer.querySelector('.variant-step.active');
    if (firstStep) {
      this.updateAvailableOptions(firstStep);
    }
  }

  ensureNavigationExists() {
    const existingNav = this.quickAddHeader.querySelector('.variant-navigation');
    if (existingNav) {
      existingNav.remove();
    }

    const navHtml = `
      <div class="variant-navigation" style="display: none;">
        <button type="button" class="btn-back">← Back</button>
      </div>
    `;

    this.quickAddHeader.insertAdjacentHTML('afterbegin', navHtml);
  }

  bindEvents() {
    this.variantContainer._clickListener = (e) => {
      if (e.target.classList.contains('variant-option') && !e.target.disabled) {
        this.handleOptionSelection(e.target);
      }
    };
    this.variantContainer.addEventListener('click', this.variantContainer._clickListener);

    this.quickAddHeader._clickListener = (e) => {
      if (e.target.classList.contains('btn-back')) {
        this.goToPreviousStep();
      }
    };
    this.quickAddHeader.addEventListener('click', this.quickAddHeader._clickListener);

    this.addToCartBtn._clickListener = () => {
      if (this.selectedVariant && !this.addToCartBtn.disabled) {
        this.addToCart(this.selectedVariant.id);
      }
    };
    this.addToCartBtn.addEventListener('click', this.addToCartBtn._clickListener);

    // Escape hides the panel (see the keydown listener below this class). It comes back when the mouse leaves
    // and focus is not in the image area, when focus leaves the image area, or when Tab moves into the panel again.
    const figure = this.productCard.querySelector('.product-card__figure');
    if (figure && !figure._quickAddDismissBound) {
      figure._quickAddDismissBound = true;
      figure.addEventListener('mouseleave', () => {
        // Keyboard focus still holds the panel open, so a mouse passing by must not undo the keyboard's Escape.
        if (!figure.contains(document.activeElement)) figure.classList.remove('is-quick-add-dismissed');
      });
      figure.addEventListener('focusout', (e) => {
        if (!figure.contains(e.relatedTarget)) figure.classList.remove('is-quick-add-dismissed');
      });
      this.panel.addEventListener('focusin', () => figure.classList.remove('is-quick-add-dismissed'));
    }
  }

  // The panel replaces or hides the button that has focus at several points. Then focus would drop to the page,
  // and the panel, which stays open only while it holds keyboard focus, would close.
  hasFocus() {
    return this.panel.contains(document.activeElement);
  }

  focusStepOption(step) {
    step?.querySelector('.variant-option:not([disabled])')?.focus({ preventScroll: true });
  }

  // The panel has tabindex="-1" in snippets/product-card.liquid, so it can take focus but Tab skips it.
  focusPanel() {
    this.panel.focus({ preventScroll: true });
  }

  handleOptionSelection(button) {
    const position = parseInt(button.dataset.optionPosition);
    const value = button.dataset.optionValue;
    const variantId = button.dataset.variantId;

    this.selectedOptions[position] = value;

    const totalSteps = this.variantContainer.querySelectorAll('.variant-step').length;

    if (totalSteps === 1) {
      if (variantId) {
        this.addToCart(variantId);
      }
      return;
    }

    const variant = this.findVariantByOptions(this.selectedOptions, this.variants);

    if (Object.keys(this.selectedOptions).length === totalSteps && variant) {
      this.addToCart(variant.id);
      return;
    }

    this.goToNextStep();
  }

  goToNextStep() {
    const steps = this.variantContainer.querySelectorAll('.variant-step');
    const currentStep = this.variantContainer.querySelector('.variant-step.active');
    const currentIndex = Array.from(steps).indexOf(currentStep);
    const hadFocus = this.hasFocus();

    if (currentIndex < steps.length - 1) {
      currentStep.classList.remove('active');
      steps[currentIndex + 1].classList.add('active');
      this.currentStep = currentIndex + 1;

      const navigation = this.quickAddHeader.querySelector('.variant-navigation');
      const quickAddTitle = this.quickAddHeader.querySelector('.quick-add-title');

      if (currentIndex + 1 > 0) {
        if (navigation) navigation.style.display = 'flex';
        if (quickAddTitle) quickAddTitle.style.display = 'none';
      }

      this.updateAvailableOptions(steps[currentIndex + 1]);
      if (hadFocus) this.focusStepOption(steps[currentIndex + 1]);
    }
  }

  goToPreviousStep() {
    const steps = this.variantContainer.querySelectorAll('.variant-step');
    const currentStep = this.variantContainer.querySelector('.variant-step.active');
    const hadFocus = this.hasFocus();

    if (steps.length === 0 || !currentStep) {
      const quickAddHeader = this.panel.querySelector('.quick-add-header');
      const quickAddTitle = this.quickAddHeader.querySelector('.quick-add-title');
      const navigation = this.quickAddHeader.querySelector('.variant-navigation');
      const addToCartActions = this.panel.querySelector('.add-to-cart-actions');

      if (quickAddHeader) quickAddHeader.style.display = 'flex';
      if (quickAddTitle) quickAddTitle.style.display = 'block';
      if (navigation) navigation.style.display = 'none';
      if (addToCartActions) addToCartActions.style.display = 'block';

      this.selectedOptions = {};
      this.selectedVariant = null;
      this.currentStep = 0;
      this.setupVariantSelector();
      if (hadFocus) {
        this.focusStepOption(this.variantContainer.querySelector('.variant-step.active'));
        if (!this.hasFocus() && this.addToCartBtn.style.display !== 'none') this.addToCartBtn.focus();
      }
      return;
    }

    const currentIndex = Array.from(steps).indexOf(currentStep);

    if (currentIndex > 0) {
      const firstOption = currentStep.querySelector('.variant-option');
      if (!firstOption) {
        return;
      }

      const currentStepPosition = parseInt(firstOption.dataset.optionPosition);

      Object.keys(this.selectedOptions).forEach((pos) => {
        if (parseInt(pos) >= currentStepPosition) {
          delete this.selectedOptions[pos];
        }
      });

      currentStep.classList.remove('active');
      steps[currentIndex - 1].classList.add('active');
      this.currentStep = currentIndex - 1;

      const navigation = this.quickAddHeader.querySelector('.variant-navigation');
      const quickAddTitle = this.quickAddHeader.querySelector('.quick-add-title');

      if (currentIndex - 1 === 0) {
        if (navigation) navigation.style.display = 'none';
        if (quickAddTitle) quickAddTitle.style.display = 'block';
      }

      this.updateAvailableOptions(steps[currentIndex - 1]);
      if (hadFocus) this.focusStepOption(steps[currentIndex - 1]);
    }
  }

  updateAvailableOptions(step) {
    const options = step.querySelectorAll('.variant-option');

    options.forEach((option) => {
      const position = parseInt(option.dataset.optionPosition);
      const value = option.dataset.optionValue;

      const testOptions = {};

      Object.entries(this.selectedOptions).forEach(([pos, val]) => {
        if (parseInt(pos) < position) {
          testOptions[pos] = val;
        }
      });

      testOptions[position] = value;

      const isAvailable = this.variants.some(
        (v) => Object.entries(testOptions).every(([p, val]) => v.options[parseInt(p) - 1] === val) && v.available,
      );
      option.classList.toggle('unavailable', !isAvailable);
      option.disabled = !isAvailable;
    });
  }

  findVariantByOptions(options, variants = this.variants) {
    return variants.find((variant) => {
      return Object.entries(options).every(([position, value]) => {
        return variant.options[position - 1] === value;
      });
    });
  }

  async addToCart(variantId) {
    const savedCurrentStep = this.currentStep;
    const savedSelectedOptions = { ...this.selectedOptions };
    const savedVariantContainer = this.variantContainer.innerHTML;
    const hadFocus = this.hasFocus();

    // The buttons are about to be replaced. Hold focus on the panel itself, so it stays open and the reader keeps
    // their place.
    if (hadFocus) this.focusPanel();
    announceCartStatus('Adding to cart');
    this.showAddingState();

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (response.ok) {
        this.showSuccess();
        announceCartStatus('Added to cart');
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
          }),
        );
        document.getElementById('cart-drawer')?.setAttribute('open', '');
      } else {
        throw new Error('FAILED TO ADD TO CART. PLEASE TRY AGAIN.');
      }
    } catch (error) {
      console.error('ADD TO CART ERROR:', error);
      this.currentStep = savedCurrentStep;
      this.selectedOptions = savedSelectedOptions;
      this.variantContainer.innerHTML = savedVariantContainer;
      this.showError();
      announceCartStatus('Failed to add to cart. Please try again.');
      // The error replaces the options. Its Back button brings them back.
      if (hadFocus) this.quickAddHeader.querySelector('.btn-back')?.focus({ preventScroll: true });
    }
  }

  showAddingState() {
    const quickAddHeader = this.panel.querySelector('.quick-add-header');
    const addToCartActions = this.panel.querySelector('.add-to-cart-actions');

    if (quickAddHeader) quickAddHeader.style.display = 'none';
    if (addToCartActions) addToCartActions.style.display = 'none';

    this.variantContainer.innerHTML = `
      <div class="adding-state">
        <div>ADDING TO CART...</div>
      </div>
    `;
  }

  showSuccess() {
    this.variantContainer.innerHTML = `
      <div class="success-state">
        <div>ADDED TO CART!</div>
      </div>
    `;

    const quickAddHeader = this.panel.querySelector('.quick-add-header');
    const quickAddTitle = this.quickAddHeader.querySelector('.quick-add-title');
    const navigation = this.quickAddHeader.querySelector('.variant-navigation');
    const addToCartActions = this.panel.querySelector('.add-to-cart-actions');

    if (quickAddHeader) quickAddHeader.style.display = 'flex';
    if (quickAddTitle) quickAddTitle.style.display = 'none';
    if (navigation) navigation.style.display = 'flex';
    if (addToCartActions) addToCartActions.style.display = 'none';
  }

  showError() {
    this.variantContainer.innerHTML = `
      <div class="error-state">
        <div>FAILED TO ADD TO CART. PLEASE TRY AGAIN.</div>
      </div>
    `;

    const quickAddHeader = this.panel.querySelector('.quick-add-header');
    const quickAddTitle = this.quickAddHeader.querySelector('.quick-add-title');
    const navigation = this.quickAddHeader.querySelector('.variant-navigation');
    const addToCartActions = this.panel.querySelector('.add-to-cart-actions');

    if (quickAddHeader) quickAddHeader.style.display = 'flex';
    if (quickAddTitle) quickAddTitle.style.display = 'none';
    if (navigation) navigation.style.display = 'flex';
    if (addToCartActions) addToCartActions.style.display = 'none';
  }
}

// Escape hides the desktop quick add panel (WCAG 1.4.13: content that appears on hover or focus can be dismissed).
// Focus can be in one card while the mouse is over another, and then both panels show. So it hides both. If focus
// is inside a panel, it moves to the card image link first, so it is not left on a hidden button.
// A dialog that closed on this Escape (its focus trap calls preventDefault) has used it, so the cards ignore it.
// One listener for every card.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || event.defaultPrevented || !window.matchMedia('(min-width: 1000px)').matches) {
    return;
  }
  const focused = document.activeElement;
  const figures = new Set([
    focused?.closest?.('.product-card-featured .product-card__figure'),
    document.querySelector('.product-card-featured .product-card__figure:hover'),
  ]);
  figures.forEach((figure) => {
    const panel = figure?.querySelector('.product-card__add-to-cart-panel');
    if (!panel || figure.classList.contains('is-quick-add-dismissed')) {
      return;
    }
    if (panel.contains(focused)) {
      figure.querySelector('.product-card__media')?.focus({ preventScroll: true });
    }
    figure.classList.add('is-quick-add-dismissed');
  });
});

window.loadProductData = async function (swatchElement, productId) {
  const productCard = swatchElement.closest('.product-card-featured');
  if (!productCard) {
    return;
  }

  if (swatchElement.classList.contains('active')) {
    return;
  }

  if (productCard.dataset.loadingProduct === productId || productCard.dataset.isLoadingProduct === 'true') {
    return;
  }

  productCard.dataset.loadingProduct = productId;
  productCard.dataset.isLoadingProduct = 'true';
  productCard.classList.add('loading');

  // aria-disabled, not disabled. A disabled button loses focus, and the browser moves focus to the page,
  // so a keyboard user loses their place. The loading check above already ignores a second click.
  const allSwatches = productCard.querySelectorAll('.product-swatch');
  allSwatches.forEach((swatch) => swatch.setAttribute('aria-disabled', 'true'));

  try {
    let variants = [];
    let options = [];

    try {
      variants = JSON.parse(swatchElement.dataset.productVariants || '[]');
    } catch (e) {
      console.error('Error parsing product variants:', e);
    }

    try {
      options = JSON.parse(swatchElement.dataset.productOptions || '[]');
    } catch (e) {
      console.error('Error parsing product options:', e);
    }

    const productData = {
      id: swatchElement.dataset.productId,
      handle: swatchElement.dataset.productHandle,
      title: swatchElement.dataset.productTitle,
      featured_image: swatchElement.dataset.productFeaturedImage,
      images: [swatchElement.dataset.productFeaturedImage, swatchElement.dataset.productSecondaryImage].filter(Boolean),
      price: parseInt(swatchElement.dataset.productPrice, 10) || 0,
      compare_at_price: parseInt(swatchElement.dataset.productComparePrice, 10) || 0,
      variants: variants,
      options: options,
    };

    await updateProductCard(productCard, productData, swatchElement);
  } catch (error) {
    console.error('Error loading product data:', error);
    productCard.classList.remove('loading');
  } finally {
    delete productCard.dataset.loadingProduct;
    delete productCard.dataset.isLoadingProduct;

    const allSwatches = productCard.querySelectorAll('.product-swatch');
    allSwatches.forEach((swatch) => swatch.removeAttribute('aria-disabled'));
  }
};

async function updateProductCard(productCard, productData, selectedSwatch) {
  return new Promise(async (resolve) => {
    let imagesToLoad = 0;
    let imagesLoaded = 0;

    const checkAllImagesLoaded = () => {
      imagesLoaded++;
      if (imagesLoaded >= imagesToLoad) {
        productCard.classList.remove('loading');
        resolve();
      }
    };

    const mainImage = productCard.querySelector('.product-card__image--primary');
    if (mainImage && productData.featured_image) {
      imagesToLoad++;

      let baseUrl = productData.featured_image;
      if (baseUrl.startsWith('//')) {
        baseUrl = 'https:' + baseUrl;
      }

      const cleanUrl = baseUrl.split('?')[0];
      const imageUrl = baseUrl.includes('?') ? baseUrl : `${cleanUrl}?width=600`;

      const newImage = new Image();
      newImage.onload = checkAllImagesLoaded;
      newImage.onerror = checkAllImagesLoaded;

      newImage.src = imageUrl;
      mainImage.src = imageUrl;
      mainImage.alt = productData.title;

      const srcset = [
        `${cleanUrl}?width=200 200w`,
        `${cleanUrl}?width=300 300w`,
        `${cleanUrl}?width=400 400w`,
        `${cleanUrl}?width=500 500w`,
        `${cleanUrl}?width=600 600w`,
        `${cleanUrl}?width=700 700w`,
        `${cleanUrl}?width=800 800w`,
      ].join(', ');
      mainImage.srcset = srcset;
    }

    const secondaryImage = productCard.querySelector('.product-card__image--secondary');
    if (secondaryImage && productData.images && productData.images.length > 1) {
      imagesToLoad++;

      let baseUrl = productData.images[1];
      if (baseUrl.startsWith('//')) {
        baseUrl = 'https:' + baseUrl;
      }

      const cleanUrl = baseUrl.split('?')[0];
      const imageUrl = baseUrl.includes('?') ? baseUrl : `${cleanUrl}?width=600`;

      const newImage = new Image();
      newImage.onload = checkAllImagesLoaded;
      newImage.onerror = checkAllImagesLoaded;

      newImage.src = imageUrl;
      secondaryImage.src = imageUrl;
      // The primary image already names the card link. The hover image stays unnamed, so screen
      // readers do not read the link name twice.
      secondaryImage.alt = '';

      const srcset = [
        `${cleanUrl}?width=200 200w`,
        `${cleanUrl}?width=300 300w`,
        `${cleanUrl}?width=400 400w`,
        `${cleanUrl}?width=500 500w`,
        `${cleanUrl}?width=600 600w`,
        `${cleanUrl}?width=700 700w`,
        `${cleanUrl}?width=800 800w`,
      ].join(', ');
      secondaryImage.srcset = srcset;
    }

    if (imagesToLoad === 0) {
      productCard.classList.remove('loading');
      resolve();
    }

    const titleElement = productCard.querySelector('.product-title a');
    if (titleElement) {
      titleElement.textContent = productData.title;
      titleElement.href = window.Shopify.routes.root + `products/${productData.handle}`;
    }

    const imageLink = productCard.querySelector('.product-card__media');
    if (imageLink) {
      imageLink.href = window.Shopify.routes.root + `products/${productData.handle}`;
    }

    // "+12 more prints" opens the product page of the print the card now shows. The count stays:
    // the card shows the same swatches after a swap, so the prints it does not show are the same.
    const moreLink = productCard.querySelector('.product-swatches__more');
    if (moreLink) {
      moreLink.href = window.Shopify.routes.root + `products/${productData.handle}`;
    }

    updateProductTag(productCard, selectedSwatch);

    const pricingContainer = productCard.querySelector('.product-pricing');
    if (pricingContainer) {
      pricingContainer.innerHTML = buildCardPriceHtml(productData);
    }

    const allSwatches = productCard.querySelectorAll('.product-swatch');
    allSwatches.forEach((swatch) => {
      swatch.classList.remove('active');
      swatch.setAttribute('aria-pressed', 'false');
    });
    selectedSwatch.classList.add('active');
    selectedSwatch.setAttribute('aria-pressed', 'true');

    productCard.dataset.productId = productData.id;
    productCard.dataset.productHandle = productData.handle;

    const addToCartPanel = productCard.querySelector('.product-card__add-to-cart-panel');
    if (addToCartPanel && productData.variants) {
      addToCartPanel.dataset.productVariants = JSON.stringify(productData.variants);
      addToCartPanel.dataset.productOptions = JSON.stringify(productData.options || []);

      const badgeText = selectedSwatch.dataset.badgeText || '';
      addToCartPanel.dataset.badgeText = badgeText;

      productCard.dataset.panelInitialized = '';

      if (!productCard.addToCartPanelInstance) {
        productCard.addToCartPanelInstance = new AddToCartPanel(productCard);
      } else {
        productCard.addToCartPanelInstance.variants = productData.variants;
        productCard.addToCartPanelInstance.productOptions = productData.options || [];
        productCard.addToCartPanelInstance.setupVariantSelector();
      }
    }

    const mobileCartIcon = productCard.querySelector('.mobile-cart-icon');
    if (mobileCartIcon && productData.variants) {
      mobileCartIcon.dataset.productVariants = JSON.stringify(productData.variants);
      mobileCartIcon.dataset.productOptions = JSON.stringify(productData.options || []);
      mobileCartIcon.dataset.productId = productData.id;
      mobileCartIcon.dataset.productHandle = productData.handle;
      mobileCartIcon.dataset.productTitle = productData.title;
      mobileCartIcon.dataset.productImage = productData.featured_image || '';
      mobileCartIcon.dataset.productPrice = productData.price;
      mobileCartIcon.dataset.productComparePrice = productData.compare_at_price || 0;
      setMobileCartIconLabel(mobileCartIcon, productData.title, productData.variants);
    }

    // The wishlist heart (assets/wishlist.js) watches these and switches to the new product.
    const wishlistButton = productCard.querySelector('svaha-wishlist-button');
    if (wishlistButton) {
      wishlistButton.dataset.productTitle = productData.title;
      wishlistButton.dataset.productHandle = productData.handle;
    }

    await updateProductReviews(productCard, productData.handle, productData.id);
  });
}

function updateProductTag(productCard, swatchElement) {
  let productTag = productCard.querySelector('.product-tag');

  let badgeText = '';

  if (swatchElement && swatchElement.dataset.badgeText) {
    badgeText = swatchElement.dataset.badgeText.toUpperCase();
  }

  if (badgeText) {
    if (!productTag) {
      const productFigure = productCard.querySelector('.product-card__figure');
      if (productFigure) {
        productTag = document.createElement('div');
        productTag.className = 'product-tag';
        productFigure.appendChild(productTag);
      } else {
        return;
      }
    }

    productTag.textContent = badgeText;
    productTag.style.display = 'block';
  } else {
    if (productTag) {
      productTag.remove();
    }
  }
}

function formatMoney(cents) {
  if (typeof cents !== 'number') return '';

  const format = window.shopMoneyFormat || '${{amount}}';

  return format
    .replace('{{amount}}', (cents / 100).toFixed(2))
    .replace('{{amount_no_decimals}}', Math.round(cents / 100))
    .replace('{{amount_with_comma_separator}}', (cents / 100).toFixed(2).replace('.', ','))
    .replace(
      '{{amount_no_decimals_with_comma_separator}}',
      Math.round(cents / 100)
        .toString()
        .replace('.', ','),
    );
}

// This function mirrors the markup in the "card-price" snippet. The card swatches and the mobile quick-add modal both
// rebuild the price in the browser. This function holds the only JS copy of that markup. Keep it in sync with the
// snippet.
function buildCardPriceHtml(productData) {
  const { price: priceAmount, compareAt, varies } = resolveCardPrice(productData);
  const price = varies ? formatFromPrice(formatMoney(priceAmount)) : formatMoney(priceAmount);
  const strings = window.themeVariables?.strings || {};
  const regularLabel = `<span class="sr-only">${strings.regularPrice || 'Regular price'}</span>`;

  if (compareAt && compareAt > priceAmount) {
    const saleLabel = `<span class="sr-only">${strings.salePrice || 'Sale price'}</span>`;

    return `
      <span class="price-compare">${regularLabel}${formatMoney(compareAt)}</span>
      <span class="price-sale">${saleLabel}${price}</span>
    `;
  }

  return `<span class="price-regular">${regularLabel}${price}</span>`;
}

// This function mirrors the price resolution in the "card-price" snippet. It takes the cheapest in-stock variant. It
// shows "From" when another in-stock variant costs more. The variant prices come from the JSON on the swatch button
// and the quick-add button. Those prices are in cents, so the code compares them directly. The function falls back to
// the product-level values in two cases: every variant is sold out, or the caller supplied no variant data. The
// snippet does the same.
function resolveCardPrice(productData) {
  const variants = productData.variants || [];
  const inStock = variants.filter((variant) => variant.available && variant.price != null);

  if (inStock.length === 0) {
    const prices = variants.map((variant) => variant.price).filter((price) => price != null);

    return {
      price: productData.price,
      compareAt: productData.compare_at_price,
      varies: new Set(prices).size > 1,
    };
  }

  const cheapest = inStock.reduce((lowest, variant) => (variant.price < lowest.price ? variant : lowest));
  const dearest = inStock.reduce((highest, variant) => (variant.price > highest.price ? variant : highest));

  return {
    price: cheapest.price,
    compareAt: cheapest.compare_at_price,
    varies: dearest.price > cheapest.price,
  };
}

function formatFromPrice(price) {
  const template = window.themeVariables?.strings?.fromPrice;

  return template ? template.replace('@@', price) : `From ${price}`;
}

async function updateProductReviews(productCard, productHandle, productId) {
  try {
    if (productCard.dataset.reviewUpdatePending === 'true') {
      return;
    }

    productCard.dataset.reviewUpdatePending = 'true';

    const ratingContainer = productCard.querySelector('.product-card__info');

    if (!ratingContainer) {
      console.warn('Rating container not found');
      productCard.dataset.reviewUpdatePending = 'false';
      return;
    }

    const productTitle = ratingContainer.querySelector('.product-title');
    const pricingElement = ratingContainer.querySelector('.product-pricing');

    const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}?view=card-reviews`);

    if (!response.ok) {
      console.warn('Card reviews view not available, skipping review update');
      productCard.dataset.reviewUpdatePending = 'false';
      reinitializeYotpoWidgets(productCard, productId);
      return;
    }

    const html = await response.text();

    if (!html || html.trim() === '') {
      console.warn('Empty response from card-reviews view');
      productCard.dataset.reviewUpdatePending = 'false';
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const newSswWidget = doc.querySelector('.ssw-avg-rate');
    const newRatingBadge = doc.querySelector('.rating-badge');

    const allExistingRatingBadges = ratingContainer.querySelectorAll('.rating-badge');
    const allExistingSswWidgets = ratingContainer.querySelectorAll('.ssw-avg-rate');

    allExistingRatingBadges.forEach((badge) => badge.remove());
    allExistingSswWidgets.forEach((widget) => widget.remove());

    if (productTitle && pricingElement) {
      if (newSswWidget) {
        pricingElement.before(newSswWidget);
      }
      if (newRatingBadge) {
        pricingElement.before(newRatingBadge);
      }
    } else if (productTitle) {
      if (newSswWidget) {
        productTitle.after(newSswWidget);
      }
      if (newRatingBadge) {
        if (newSswWidget) {
          newSswWidget.after(newRatingBadge);
        } else {
          productTitle.after(newRatingBadge);
        }
      }
    } else {
      if (newSswWidget) {
        ratingContainer.appendChild(newSswWidget);
      }
      if (newRatingBadge) {
        ratingContainer.appendChild(newRatingBadge);
      }
    }

    productCard.dataset.reviewUpdatePending = 'false';
    reinitializeYotpoWidgets(productCard, productId);
  } catch (error) {
    console.error('Error updating product reviews:', error);
    productCard.dataset.reviewUpdatePending = 'false';
  }
}

function reinitializeYotpoWidgets(productCard, productId) {
  try {
    if (typeof window.yotpo !== 'undefined' || typeof window.yotpoWidgetsContainer !== 'undefined') {
      const yotpoWidgets = productCard.querySelectorAll('[data-yotpo-product-id], .yotpo');

      yotpoWidgets.forEach((widget) => {
        if (widget.dataset.yotpoProductId) {
          widget.dataset.yotpoProductId = productId;
        }
        if (widget.dataset.productId) {
          widget.dataset.productId = productId;
        }
      });

      if (
        typeof window.yotpoWidgetsContainer !== 'undefined' &&
        typeof window.yotpoWidgetsContainer.initWidgets === 'function'
      ) {
        window.yotpoWidgetsContainer.initWidgets();
      } else if (typeof window.yotpo !== 'undefined' && typeof window.yotpo.initWidgets === 'function') {
        window.yotpo.initWidgets();
      }
    }
  } catch (error) {
    console.error('Error reinitializing Yotpo widgets:', error);
  }
}

class MobileVariantModal {
  constructor() {
    this.modal = document.getElementById('mobile-variant-modal');
    if (!this.modal) {
      console.warn('MobileVariantModal: Modal element not found');
      this.isValid = false;
      return;
    }

    this.errorContainer = this.modal.querySelector('.mobile-modal-error');
    this.productImage = this.modal.querySelector('.mobile-product-image');
    this.productTitle = this.modal.querySelector('.mobile-product-title');
    this.productPrice = this.modal.querySelector('.mobile-product-price');
    this.variantSteps = this.modal.querySelector('.mobile-variant-steps');
    this.backButton = this.modal.querySelector('.mobile-variant-back');
    this.status = this.modal.querySelector('.mobile-variant-status');
    this.heading = this.modal.querySelector('#mobile-variant-modal-title');

    if (
      !this.errorContainer ||
      !this.productImage ||
      !this.productTitle ||
      !this.productPrice ||
      !this.variantSteps ||
      !this.backButton ||
      !this.status ||
      !this.heading
    ) {
      console.warn('MobileVariantModal: Required child elements not found');
      this.isValid = false;
      return;
    }

    this.currentProduct = null;
    this.variants = [];
    this.selectedOptions = {};
    this.currentStep = 0;
    this.options = [];
    this.productOptions = [];
    this.isValid = true;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  // x-modal handles Escape, the overlay, the close button, scroll lock and focus return. Only the steps are ours.
  bindEvents() {
    // Only add listeners once - prevent duplication on reinit
    if (!this._stepsClickHandler) {
      this._stepsClickHandler = (e) => {
        const option = e.target.closest('.mobile-variant-option');
        if (option && !option.disabled) {
          this.handleOptionSelection(option);
        }
      };
      this._backClickHandler = () => this.goToPreviousStep();

      this.variantSteps.addEventListener('click', this._stepsClickHandler);
      this.backButton.addEventListener('click', this._backClickHandler);
    }
  }

  destroy() {
    if (this._stepsClickHandler) {
      this.variantSteps.removeEventListener('click', this._stepsClickHandler);
      this.backButton.removeEventListener('click', this._backClickHandler);
      this._stepsClickHandler = null;
      this._backClickHandler = null;
    }
  }

  async open(productData, variants, trigger) {
    this.currentProduct = productData;
    this.trigger = trigger;
    this.variants = variants;
    this.selectedOptions = {};
    this.currentStep = 0;
    this.productOptions = productData.options || [];

    this.productImage.src = productData.featured_image
      ? productData.featured_image.replace(/\?.*$/, '') + '?width=160'
      : '';
    // The title is next to the image, so the image adds nothing for a screen reader.
    this.productImage.alt = '';
    this.productTitle.textContent = productData.title;

    this.updatePricing(productData);

    this.hideError();
    this.status.textContent = '';
    this.setupVariantSteps();

    // x-modal comes from theme.js. It loads before this file today, but do not depend on the load order.
    await customElements.whenDefined('x-modal');

    // The card button opened the panel. Focus goes back to it on close.
    this.modal.svahaNextOpener = trigger;
    await this.modal.show();
    // Start on the selected option. The theme moves focus in later, and leaves it alone because it is already inside.
    this.focusStep(this.currentStep);
  }

  close() {
    this.selectedOptions = {};
    this.currentStep = 0;
    this.updateBackButton();
    this.hideError();
    return this.modal.hide();
  }

  // Focus the selected option of a step, or its first option that can be chosen. The heading if there is none.
  focusStep(index) {
    const step = this.variantSteps.querySelectorAll('.mobile-variant-step')[index];
    const option =
      step?.querySelector('.mobile-variant-option.selected:not([disabled])') ||
      step?.querySelector('.mobile-variant-option:not([disabled])');
    if (option) {
      option.focus({ preventScroll: true });
    } else {
      this.focusHeading();
    }
  }

  // The heading has tabindex="-1" in snippets/product-card-mobile-atc-panel.liquid, so it can take focus.
  focusHeading() {
    this.heading.focus({ preventScroll: true });
  }

  // The selected look is a class. aria-pressed tells a screen reader the same thing.
  setSelected(option, selected) {
    option.classList.toggle('selected', selected);
    option.setAttribute('aria-pressed', String(selected));
  }

  updateBackButton() {
    this.backButton.hidden = this.currentStep === 0;
  }

  updatePricing(productData) {
    // The productData of the modal carries options but no variants. Fall back to the variants that opened the modal.
    // Without that fallback, "From" never shows here, even when the product has larger sizes that cost more.
    this.productPrice.innerHTML = buildCardPriceHtml({
      ...productData,
      variants: productData.variants || this.variants,
    });
  }

  setupVariantSteps() {
    const realVariants = getRealVariants(this.variants);

    if (realVariants.length === 0) {
      if (this.variants[0]?.available) {
        this.addToCart(this.variants[0].id);
      }
      return;
    }

    this.options = this.getProductOptions(realVariants);

    if (this.options.length === 0) {
      if (this.variants[0]?.available) {
        this.addToCart(this.variants[0].id);
      }
      return;
    }

    this.renderVariantSteps();
  }

  getProductOptions(variants) {
    const optionsMap = new Map();

    variants.forEach((variant) => {
      variant.options.forEach((value, index) => {
        if (value && value !== null && value !== 'Default Title') {
          const position = index + 1;
          if (!optionsMap.has(position)) {
            optionsMap.set(position, {
              name: this.getOptionName(position),
              values: new Set(),
            });
          }
          optionsMap.get(position).values.add(value);
        }
      });
    });

    return Array.from(optionsMap.entries()).map(([position, data]) => ({
      position,
      name: data.name,
      values: Array.from(data.values),
    }));
  }

  getOptionName(position) {
    if (this.productOptions && this.productOptions.length > 0) {
      const option = this.productOptions.find((opt) => opt.position === position);
      if (option && option.name) {
        return option.name;
      }
    }

    const defaultNames = {
      1: 'Size',
      2: 'Color',
      3: 'Style',
    };
    return defaultNames[position] || `Option ${position}`;
  }

  renderVariantSteps() {
    const stepsHtml = this.options
      .map(
        (option, index) => `
      <div class="mobile-variant-step ${index === 0 ? 'active' : ''}" data-step="${index}">
        <div class="mobile-variant-step-title" id="mobile-variant-step-title-${index}">SELECT ${escapeHtml(option.name).toUpperCase()}</div>
        <div class="mobile-variant-options" role="group" aria-labelledby="mobile-variant-step-title-${index}">
          ${option.values
            .map((value) => {
              const isAvailable = this.isOptionAvailable(option.position, value);
              return `
              <button type="button" 
                      class="mobile-variant-option ${!isAvailable ? 'unavailable' : ''}" 
                      data-option-position="${option.position}" 
                      data-option-value="${escapeHtml(value)}"
                      aria-pressed="false"
                      ${!isAvailable ? 'disabled' : ''}>
                ${escapeHtml(value)}
              </button>
            `;
            })
            .join('')}
        </div>
      </div>
    `,
      )
      .join('');

    this.variantSteps.innerHTML = stepsHtml;

    this.updateBackButton();
  }

  isOptionAvailable(position, value) {
    const testOptions = {};

    Object.entries(this.selectedOptions).forEach(([pos, val]) => {
      if (parseInt(pos) < position) {
        testOptions[pos] = val;
      }
    });

    testOptions[position] = value;

    return this.variants.some((variant) => {
      const matches = Object.entries(testOptions).every(([pos, val]) => {
        return variant.options[pos - 1] === val;
      });
      return matches && variant.available;
    });
  }

  handleOptionSelection(button) {
    const position = parseInt(button.dataset.optionPosition);
    const value = button.dataset.optionValue;

    this.hideError();

    this.selectedOptions[position] = value;

    const currentStep = button.closest('.mobile-variant-step');
    currentStep.querySelectorAll('.mobile-variant-option').forEach((opt) => this.setSelected(opt, opt === button));

    if (Object.keys(this.selectedOptions).length === this.options.length) {
      const selectedVariant = this.findVariantByOptions(this.selectedOptions);
      if (selectedVariant && selectedVariant.available) {
        this.addToCart(selectedVariant.id);
      } else {
        this.showError('THIS COMBINATION IS NOT AVAILABLE.');
      }
    } else {
      this.goToNextStep();
    }
  }

  goToNextStep() {
    const steps = this.variantSteps.querySelectorAll('.mobile-variant-step');
    const currentStep = this.variantSteps.querySelector('.mobile-variant-step.active');
    const currentIndex = Array.from(steps).indexOf(currentStep);

    if (currentIndex < steps.length - 1) {
      currentStep.classList.remove('active');
      steps[currentIndex + 1].classList.add('active');
      this.currentStep = currentIndex + 1;
      this.updateBackButton();

      this.updateAvailableOptions(steps[currentIndex + 1]);
      // The step that had focus is now hidden. Without this, focus drops to the page behind the panel.
      this.focusStep(this.currentStep);
    }
  }

  goToPreviousStep() {
    const steps = this.variantSteps.querySelectorAll('.mobile-variant-step');
    const currentStep = this.variantSteps.querySelector('.mobile-variant-step.active');

    if (!currentStep) {
      return;
    }

    const currentIndex = Array.from(steps).indexOf(currentStep);

    if (currentIndex > 0) {
      const firstOption = currentStep.querySelector('.mobile-variant-option');
      if (!firstOption) {
        return;
      }

      const currentStepPosition = parseInt(firstOption.dataset.optionPosition);

      Object.keys(this.selectedOptions).forEach((pos) => {
        if (parseInt(pos) >= currentStepPosition) {
          delete this.selectedOptions[pos];
        }
      });

      currentStep.querySelectorAll('.mobile-variant-option').forEach((opt) => this.setSelected(opt, false));

      currentStep.classList.remove('active');
      steps[currentIndex - 1].classList.add('active');
      this.currentStep = currentIndex - 1;
      this.updateBackButton();

      this.updateAvailableOptions(steps[currentIndex - 1]);

      this.hideError();
      this.focusStep(this.currentStep);
    }
  }

  updateAvailableOptions(step) {
    const options = step.querySelectorAll('.mobile-variant-option');

    options.forEach((option) => {
      const position = parseInt(option.dataset.optionPosition);
      const value = option.dataset.optionValue;

      const isAvailable = this.isOptionAvailable(position, value);

      option.classList.toggle('unavailable', !isAvailable);
      option.disabled = !isAvailable;
    });
  }

  findVariantByOptions(options) {
    return this.variants.find((variant) => {
      return Object.entries(options).every(([position, value]) => {
        return variant.options[position - 1] === value;
      });
    });
  }

  async addToCart(variantId) {
    if (!variantId) {
      this.showError('NO VARIANT SELECTED');
      return;
    }

    const savedCurrentStep = this.currentStep;
    const savedSelectedOptions = { ...this.selectedOptions };

    // The option buttons are about to be replaced. Keep focus inside the panel, on its heading.
    this.focusHeading();
    this.showAddingState();

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (response.ok) {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
          }),
        );
        // Close the panel before the cart drawer opens. Both trap focus. If the panel closed after the drawer
        // opened, it would give focus back to the card button, behind the drawer.
        await this.close();
        announceCartStatus('Added to cart');
        const cartDrawer = document.getElementById('cart-drawer');
        if (cartDrawer) {
          // The option buttons are gone by now. Focus goes back to the card button when the cart closes.
          cartDrawer.svahaNextOpener = this.trigger;
          cartDrawer.setAttribute('open', '');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message);
      }
    } catch (error) {
      console.error('ADD TO CART ERROR:', error);
      this.showError('FAILED TO ADD TO CART. PLEASE TRY AGAIN.');

      this.currentStep = savedCurrentStep;
      this.selectedOptions = savedSelectedOptions;
      this.restoreVariantSteps();
      this.status.textContent = '';
      // The shopper may have closed the panel during the add. Then focus is already back on the card button.
      if (this.modal.open) {
        this.focusStep(this.currentStep);
      }
    }
  }

  showAddingState() {
    this.backButton.hidden = true;
    this.status.textContent = 'Adding to cart';

    this.variantSteps.innerHTML = `
      <div class="adding-state">
        <div>ADDING TO CART...</div>
      </div>
    `;
  }

  restoreVariantSteps() {
    if (!this.options || this.options.length === 0) {
      this.variantSteps.innerHTML = '';
      return;
    }

    this.renderVariantSteps();

    const steps = this.variantSteps.querySelectorAll('.mobile-variant-step');
    if (steps.length > 0 && this.currentStep < steps.length) {
      steps.forEach((step, index) => {
        if (index === this.currentStep) {
          step.classList.add('active');
        } else {
          step.classList.remove('active');
        }
      });

      steps.forEach((step) => {
        const options = step.querySelectorAll('.mobile-variant-option');
        options.forEach((option) => {
          const position = parseInt(option.dataset.optionPosition);
          const value = option.dataset.optionValue;

          this.setSelected(option, this.selectedOptions[position] === value);
        });
      });

      this.updateBackButton();

      if (steps[this.currentStep]) {
        this.updateAvailableOptions(steps[this.currentStep]);
      }
    }
  }

  // The box has role="alert", so screen readers read the message as it appears.
  showError(message) {
    this.errorContainer.querySelector('.error-message').textContent = message;
    this.errorContainer.hidden = false;
  }

  hideError() {
    this.errorContainer.hidden = true;
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') return '';

    const format = window.shopMoneyFormat || '${{amount}}';

    return format
      .replace('{{amount}}', (cents / 100).toFixed(2))
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', (cents / 100).toFixed(2).replace('.', ','))
      .replace(
        '{{amount_no_decimals_with_comma_separator}}',
        Math.round(cents / 100)
          .toString()
          .replace('.', ','),
      );
  }
}

function initializeProductCards(container = document) {
  if (window.productCardManager) {
    window.productCardManager.initializeProductCards(container);
  } else {
    const productCards = container.querySelectorAll('.product-card-featured');
    productCards.forEach((card) => {
      if (!card.dataset.panelInitialized) {
        if (!card.addToCartPanelInstance) {
          new AddToCartPanel(card);
        }
        card.dataset.panelInitialized = 'true';
      }
    });

    initializeMobileModalFallback();
    getCartStatusRegion();

    const mobileCartIcons = container.querySelectorAll('.mobile-cart-icon');
    mobileCartIcons.forEach((icon) => {
      if (!icon.dataset.clickHandlerAttached) {
        icon.addEventListener('click', handleMobileCartClick);
        icon.dataset.clickHandlerAttached = 'true';
      }
    });
  }
}

function initializeMobileModalFallback() {
  if (window.mobileVariantModal) {
    const modalElement = document.getElementById('mobile-variant-modal');
    if (!modalElement || window.mobileVariantModal.modal !== modalElement) {
      if (window.mobileVariantModal.destroy) {
        window.mobileVariantModal.destroy();
      }
      window.mobileVariantModal = null;
    }
  }

  if (!window.mobileVariantModal && document.getElementById('mobile-variant-modal')) {
    window.mobileVariantModal = new MobileVariantModal();
  }
}

// Shopify gives a product without options one variant called "Default Title". Such a product has no real variants.
function getRealVariants(variants) {
  return variants.filter(
    (variant) => variant.title !== 'Default Title' && variant.options.some((option) => option !== 'Default Title'),
  );
}

// The card cart button shows only an icon, so its name comes from aria-label. snippets/product-card.liquid sets the
// first name. This sets it again after a swatch swap, from the same translated texts.
function setMobileCartIconLabel(button, title, variants) {
  const hasOptions = getRealVariants(variants).length > 0;
  const prefix = hasOptions ? button.dataset.labelChoose : button.dataset.labelAdd;
  if (!prefix) {
    return;
  }
  button.setAttribute('aria-label', `${prefix}: ${title}`);
  if (hasOptions) {
    button.setAttribute('aria-haspopup', 'dialog');
  } else {
    button.removeAttribute('aria-haspopup');
  }
}

async function handleMobileCartClick(event) {
  const button = event.currentTarget;
  const productCard = button.closest('.product-card-featured');

  if (!productCard) {
    console.error('Product card not found');
    return;
  }

  if (!window.mobileVariantModal) {
    console.warn('Mobile variant modal not initialized, attempting to initialize...');
    initializeMobileModalFallback();
  }

  if (!window.mobileVariantModal || !window.mobileVariantModal.isValid) {
    console.error('Mobile variant modal not available or invalid');
    return;
  }

  const productId = button.dataset.productId;
  const productHandle = button.dataset.productHandle;

  let variants = [];
  let productOptions = [];

  try {
    variants = JSON.parse(button.dataset.productVariants || '[]');
  } catch (e) {
    console.error('Error parsing product variants:', e);
  }

  try {
    productOptions = JSON.parse(button.dataset.productOptions || '[]');
  } catch (e) {
    console.error('Error parsing product options:', e);
  }

  try {
    const productData = {
      id: productId,
      handle: productHandle,
      title: button.dataset.productTitle || 'Product',
      featured_image: button.dataset.productImage || '',
      price: parseInt(button.dataset.productPrice, 10) || 0,
      compare_at_price: parseInt(button.dataset.productComparePrice, 10) || 0,
      options: productOptions,
    };

    const realVariants = getRealVariants(variants);

    if (realVariants.length === 0 && variants[0]?.id) {
      try {
        await directAddToCart(variants[0].id);
      } catch (error) {
        console.error('Direct add to cart failed:', error);
      }
    } else {
      window.mobileVariantModal.open(productData, variants, button);
    }
  } catch (error) {
    console.error('Error handling mobile cart click:', error);
  }
}

// Screen readers do not read a toast that is added and removed. One status area on the page carries the result of
// every quick add instead. It is made when the cards are set up, not when it first speaks: a status area added at
// the moment it gets text is often not read. The text is plain sentence case, so no screen reader spells it out.
function getCartStatusRegion() {
  let region = document.getElementById('cart-status-announcer');
  if (!region) {
    region = document.createElement('div');
    region.id = 'cart-status-announcer';
    region.className = 'sr-only';
    region.setAttribute('role', 'status');
    document.body.appendChild(region);
  }
  return region;
}

let cartStatusTimer;

// Empty first, then the message a moment later, so the same message twice in a row is read both times. A newer
// message cancels one still waiting, so "Adding" cannot arrive after "Added".
function announceCartStatus(message) {
  const region = getCartStatusRegion();
  clearTimeout(cartStatusTimer);
  region.textContent = '';
  cartStatusTimer = setTimeout(() => {
    region.textContent = message;
  }, 100);
}

function showNotification(message, isError = false) {
  const notification = document.createElement('div');
  notification.className = isError ? 'cart-notification error' : 'cart-notification';
  notification.textContent = message;
  document.body.appendChild(notification);

  setTimeout(() => {
    notification.remove();
  }, 3000);
}

async function directAddToCart(variantId) {
  if (!variantId) {
    return;
  }

  try {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
      }),
    });

    if (response.ok) {
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
        }),
      );
      document.getElementById('cart-drawer')?.setAttribute('open', '');
      showNotification('ADDED TO CART!');
      announceCartStatus('Added to cart');
    } else {
      throw new Error('FAILED TO ADD TO CART. PLEASE TRY AGAIN.');
    }
  } catch (error) {
    console.error('DIRECT ADD TO CART ERROR:', error);
    showNotification('ERROR ADDING TO CART. PLEASE TRY AGAIN.', true);
    announceCartStatus('Error adding to cart. Please try again.');
  }
}

class ProductCardManager {
  constructor() {
    this.initialized = false;
    this.observer = null;
    this.ajaxinateCallbacks = new Set();
    this.init();
  }

  init() {
    if (this.initialized) return;

    getCartStatusRegion();
    this.initializeProductCards();

    this.setupMutationObserver();

    this.setupAjaxinateCallback();

    this.setupFacetUpdateListener();

    this.initialized = true;
  }

  initializeProductCards(container = document) {
    const productCards = container.querySelectorAll('.product-card-featured');
    productCards.forEach((card) => {
      if (!card.dataset.panelInitialized) {
        if (!card.addToCartPanelInstance) {
          new AddToCartPanel(card);
        }
        card.dataset.panelInitialized = 'true';
      }
    });

    this.initializeMobileModal();

    const mobileCartIcons = container.querySelectorAll('.mobile-cart-icon');
    mobileCartIcons.forEach((icon) => {
      if (!icon.dataset.clickHandlerAttached) {
        icon.addEventListener('click', handleMobileCartClick);
        icon.dataset.clickHandlerAttached = 'true';
      }
    });
  }

  initializeMobileModal() {
    if (window.mobileVariantModal) {
      const modalElement = document.getElementById('mobile-variant-modal');
      if (!modalElement || window.mobileVariantModal.modal !== modalElement) {
        if (window.mobileVariantModal.destroy) {
          window.mobileVariantModal.destroy();
        }
        window.mobileVariantModal = null;
      }
    }

    if (!window.mobileVariantModal && document.getElementById('mobile-variant-modal')) {
      window.mobileVariantModal = new MobileVariantModal();
    }
  }

  setupMutationObserver() {
    const targetNode = document.querySelector('#AjaxinateContainer, .product-list');
    if (!targetNode) return;

    this.observer = new MutationObserver((mutations) => {
      let shouldReinitialize = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList?.contains('product-card-featured') || node.querySelector?.('.product-card-featured')) {
                shouldReinitialize = true;
              }
            }
          });
        }
      });

      if (shouldReinitialize) {
        setTimeout(() => {
          this.initializeProductCards();
        }, 100);
      }
    });

    this.observer.observe(targetNode, {
      childList: true,
      subtree: true,
    });
  }

  setupAjaxinateCallback() {
    const originalAjaxinate = window.Ajaxinate;
    if (!originalAjaxinate) return;

    window.Ajaxinate = function (config) {
      const settings = config || {};
      const originalCallback = settings.callback;

      settings.callback = function (responseXML) {
        if (originalCallback && typeof originalCallback === 'function') {
          originalCallback(responseXML);
        }

        setTimeout(() => {
          window.productCardManager.initializeProductCards();
        }, 100);
      };

      return new originalAjaxinate(settings);
    };

    window.Ajaxinate.prototype = originalAjaxinate.prototype;

    document.addEventListener('ajaxinate:loaded', () => {
      setTimeout(() => {
        this.initializeProductCards();
      }, 100);
    });
  }

  setupFacetUpdateListener() {
    document.addEventListener('facet:update', () => {
      setTimeout(() => {
        if (window.mobileVariantModal) {
          if (window.mobileVariantModal.destroy) {
            window.mobileVariantModal.destroy();
          }
          window.mobileVariantModal = null;
        }
        this.initializeProductCards();
      }, 500);
    });

    document.documentElement.addEventListener('theme:loading:end', () => {
      setTimeout(() => {
        if (window.mobileVariantModal) {
          if (window.mobileVariantModal.destroy) {
            window.mobileVariantModal.destroy();
          }
          window.mobileVariantModal = null;
        }
        this.initializeProductCards();
      }, 200);
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.initialized = false;
  }
}

if (typeof window !== 'undefined') {
  window.AddToCartPanel = AddToCartPanel;
  window.initializeProductCards = initializeProductCards;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.productCardManager = new ProductCardManager();
    });
  } else {
    window.productCardManager = new ProductCardManager();
  }
}
